import { useReducer, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Label } from '@iwoca/orion';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { FormProvider, Resolver, useForm } from 'react-hook-form';

import { Auth3DSecureModal } from './Auth3DSecureModal/Auth3DSecureModal';
import { FormValues } from './StripeAddPaymentMethodForm.types';
import { StripeProvider } from './StripeProvider';
import { stripeStyles } from './stripeStyles';
import { submitForm } from './submitForm';
import { validateFormFields } from './validateFormFields';
import { useGetFundingRequirement } from '../../../../../api/lending/lapiHooks';
import { HookFormInput } from '../../../../../components/Input/Input';
import { InputError } from '../../../../../components/InputError/InputError';
import { useStateKey } from '../../../../../hooks/useStateKey.hook';
import { WhiteLabelOverride } from '../../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useNavigateToNextRequirement } from '../../../hooks/useNavigateToNextRequirement';

const stripeResolver: Resolver<FormValues> = (values) => {
  const errors = validateFormFields(values);
  const hasErrors = Object.keys(errors).length > 0;
  return {
    values: hasErrors ? {} : values,
    errors: Object.fromEntries(
      Object.entries(errors).map(([key, message]) => [
        key,
        { type: 'validate', message },
      ]),
    ),
  };
};

export const StripeAddPaymentMethodForm = () => {
  const { goToNextRequirement } = useNavigateToNextRequirement();
  const forceUpdate = useReducer((x) => x + 1, 0)[1];
  const { fetchFundingRequirements } = useGetFundingRequirement({
    queryOptions: {
      enabled: false,
    },
  });
  const { stateKey } = useStateKey();
  const [submitError, setSubmitError] = useState<string | null>();
  const [stripe3DSecureRedirectUrl, setStripe3DSecureRedirectUrl] = useState<
    string | null
  >();
  const confirmStripe3DSecureCompleteRef = useRef<() => void>(null);

  const handleStripe3DSecureRequired = (url: string): Promise<void> => {
    return new Promise((resolve) => {
      setStripe3DSecureRedirectUrl(url);
      confirmStripe3DSecureCompleteRef.current = () => resolve();
    });
  };

  const handleStripe3DSecureComplete = () => {
    confirmStripe3DSecureCompleteRef.current!();
    setStripe3DSecureRedirectUrl(null);
  };

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      billingPostcode: '',
      cardholderName: '',
    },
    resolver: stripeResolver,
  });

  return (
    <StripeProvider>
      {({ stripe, elements }) => {
        const onSubmit = methods.handleSubmit(async (values) => {
          setSubmitError(null);

          try {
            await submitForm({
              stateKey: stateKey!,
              billingPostcode: values.billingPostcode,
              cardholderName: values.cardholderName,
              stripe: stripe!,
              cardNumberElement: elements!.getElement('cardNumber')!,
              onStripe3DSecureRequired: handleStripe3DSecureRequired,
            });
            await fetchFundingRequirements();
            forceUpdate();

            tracking.ampli.linkedRepaymentCard();

            goToNextRequirement();
          } catch (error) {
            // @ts-expect-error
            setSubmitError(error.message);
          }
        });

        const {
          errors,
          touchedFields,
          isValid,
          isValidating,
          isDirty,
          isSubmitting,
        } = methods.formState;

        return (
          <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
              <div className="mb-l grid grid-cols-[3fr_1fr_1fr] gap-l">
                <div>
                  <Label className="mb-m">Card number</Label>
                  <CardNumberElement
                    onChange={(event) => {
                      methods.setValue('cardNumber', event, {
                        shouldValidate: true,
                        shouldTouch: true,
                        shouldDirty: true,
                      });
                    }}
                    options={{
                      style: stripeStyles,
                      showIcon: true,
                    }}
                  />
                  <InputError
                    isVisible={Boolean(touchedFields.cardNumber)}
                    error={errors.cardNumber?.message}
                  />
                </div>

                <div>
                  <Label className="mb-m">Expiry</Label>
                  <CardExpiryElement
                    onChange={(event) => {
                      methods.setValue('cardExpiry', event, {
                        shouldValidate: true,
                        shouldTouch: true,
                        shouldDirty: true,
                      });
                    }}
                    options={{
                      style: stripeStyles,
                    }}
                  />
                  <InputError
                    isVisible={Boolean(touchedFields.cardExpiry)}
                    error={errors.cardExpiry?.message}
                  />
                </div>

                <div>
                  <Label className="mb-m">CVC</Label>
                  <CardCvcElement
                    onChange={(event) => {
                      methods.setValue('cardCvc', event, {
                        shouldValidate: true,
                        shouldTouch: true,
                        shouldDirty: true,
                      });
                    }}
                    options={{
                      style: stripeStyles,
                    }}
                  />
                  <InputError
                    isVisible={Boolean(touchedFields.cardCvc)}
                    error={errors.cardCvc?.message}
                  />
                </div>
              </div>

              <div className="mb-2xl grid grid-cols-2 gap-l">
                <div>
                  <HookFormInput
                    type="text"
                    label="Billing postcode"
                    name="billingPostcode"
                    placeholder="e.g. N6 6NG"
                    showError={false}
                  />
                  <InputError
                    isVisible={touchedFields.billingPostcode || false}
                    error={errors.billingPostcode?.message}
                  />
                </div>

                <div>
                  <HookFormInput
                    type="text"
                    label="Cardholder name"
                    name="cardholderName"
                    placeholder="John Smith"
                    showError={false}
                  />
                  <InputError
                    isVisible={touchedFields.cardholderName || false}
                    error={errors.cardholderName?.message}
                  />
                </div>
              </div>

              <WhiteLabelOverride
                componentType="button"
                disabled={isValidating || !(isValid && isDirty)}
              >
                <Button
                  disabled={isValidating || !(isValid && isDirty)}
                  loading={isSubmitting}
                >
                  Add debit card now
                </Button>
              </WhiteLabelOverride>

              <InputError
                isVisible={submitError !== undefined}
                error={submitError || ''}
              />

              {stripe3DSecureRedirectUrl && (
                <Auth3DSecureModal
                  auth3DSRedirectUrl={stripe3DSecureRedirectUrl}
                  onAuth3DSecureComplete={handleStripe3DSecureComplete}
                />
              )}
            </form>
          </FormProvider>
        );
      }}
    </StripeProvider>
  );
};
